import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { NOTIFICATION_TYPE } from 'src/modules/Notification';
import { ROUTE_TYPES_VALUES } from 'src/modules/Restaurant';
import { useRouter } from 'next/router';

import Orders from 'src/components/Orders';
import OrderSummaryButtons from 'src/components/OrderSummaryButtons';

import Button from 'src/components/Button';
import theme from 'src/styles/components/OrderStatus.module.scss';

const OrderStatus = ({
    emitMessage,
    dismissFirstMessage,
    context,
    connectOrdersSocket,
    disconnectOrdersSocket,
    connectBillSocket,
    disconnectBillSocket,
    restaurant: { name },
    table,
    notification,
    pushNotification,
    reinitialize,
    initialize,
    fingerprint,
    sessionError,
    slug,
    clearMessages,
    isAnyOrder,
    orderStatus,
    shouldShowSessionEndMessage
}) => {
    const intl = useIntl();
    const [areOrdersShowed, changeOrdersShowed] = useState(false);
    const { replace, query } = useRouter();

    useEffect(() => {
        if (sessionError && !shouldShowSessionEndMessage) {
            emitMessage({
                type: 'info',
                title: (
                    <>
                        {intl.formatMessage({ id: 'App.closeSessionTitle' })}
                    </>),
                subtitle: (
                    <>
                        {intl.formatMessage({ id: 'App.informationSubtitle' })}
                    </>),
                message: (
                    <div className={theme.popupDescription}>
                        {intl.formatMessage({ id: 'App.closeSessionDescription' })}
                    </div>
                ),
                actions: (
                    <div className={theme.actionContainer}>
                        <Button
                          className={theme.action}
                          onClick={dismissFirstMessage}
                          accent
                          raised
                        >
                            {intl.formatMessage({ id: 'App.ok' })}
                        </Button>
                    </div>
                ),
                isEscKeyDownEnabled: true,
                overflow: true,
                shouldBeFirst: true,
                backgroundIcon: 'icon-info'
            });
        }
    }, [sessionError, shouldShowSessionEndMessage]);

    useEffect(() => {
        if (context) {
            const { sessionId, routeType } = context;

            if (routeType === ROUTE_TYPES_VALUES.BILL_WAIT) {
                replace(`/restaurant/${slug}`, undefined, { shallow: true });
            }

            if ((routeType === ROUTE_TYPES_VALUES.RESTAURANT
                || routeType === ROUTE_TYPES_VALUES.BILL_STATUS)) {
                connectOrdersSocket({ sessionId });
            }

            if (routeType === ROUTE_TYPES_VALUES.BILL_WAIT) {
                connectBillSocket({ sessionId });
            }
        }

        return () => {
            disconnectOrdersSocket();
            disconnectBillSocket();
        };
    }, [context]);

    useEffect(() => {
        if (shouldShowSessionEndMessage) {
            emitMessage({
                type: 'info',
                title: (
                    <>
                        {intl.formatMessage({ id: 'App.closeBillTitle' })}
                    </>),
                subtitle: (
                    <>
                        {intl.formatMessage({ id: 'App.informationSubtitle' })}
                    </>),
                message: (
                    <div className={theme.popupDescription}>
                        {intl.formatMessage({ id: 'App.closeBillDescription' })}
                    </div>
                ),
                actions: (
                    <div className={theme.actionContainer}>
                        <Button
                          className={theme.action}
                          onClick={clearMessages}
                          accent
                          raised
                        >
                            {intl.formatMessage({ id: 'App.ok' })}
                        </Button>
                    </div>
                ),
                isEscKeyDownEnabled: true,
                onDismiss: clearMessages,
                overflow: true,
                shouldBeFirst: true,
                backgroundIcon: 'icon-info'
            });
        }
    }, [shouldShowSessionEndMessage]);

    useEffect(() => {
        if (reinitialize) {
            initialize(query.context, fingerprint);

            disconnectBillSocket();
        }
    }, [reinitialize]);

    useEffect(() => {
        if (notification === null) {
            return;
        }

        pushNotification({
            type: NOTIFICATION_TYPE.ACCEPT,
            invert: areOrdersShowed,
            message: (
                <div className={theme.notification}>
                    <div className={theme.notificationIcon}>
                        <span className={notification.notificationIcon} />
                    </div>
                    {intl.formatMessage({
                        id: notification.notificationMessageTranslationKey
                    }, notification.notificationMessageTranslationParameters)}
                </div>
            ),
            timeout: 3000
        });
    }, [notification]);

    const showOrders = () => {
        changeOrdersShowed(true);

        const title = table.name
            ? `${table.name} - nr ${table.number}`
            : `nr ${table.number}`;

        emitMessage({
            type: 'small',
            title,
            subtitle: name,
            className: theme.orderModal,
            message: (
                <Orders />
            ),
            actions: (
                <OrderSummaryButtons />
            ),
            isEscKeyDownEnabled: true,
            isDismissButtonEnabled: true,
            overflow: true,
            onDismiss: () => changeOrdersShowed(false)
        });
    };

    return (
        <div className={classnames(
              theme.container,
              {
                  [theme.visible]: ((context.routeType === ROUTE_TYPES_VALUES.RESTAURANT
                      || context.routeType === ROUTE_TYPES_VALUES.BILL_STATUS)
                  && isAnyOrder && !sessionError)
              }
          )}
        >
            <div className={theme.content}>
                <div className={theme.buttonsContainer}>
                    <Button
                      className={classnames(
                        theme.button,
                        theme.billButton,
                        {
                            [theme.active]: orderStatus.bill.isActive,
                            [theme.disabled]: orderStatus.bill.isDisabled
                        }
                      )}
                      onClick={showOrders}
                    >
                        <span className={classnames('icon-bill', {
                            [theme.disabled]: orderStatus.bill.isDisabled
                          })}
                        />
                        {orderStatus.bill.action && (
                            <>
                                <span className={theme.description}>{intl.formatMessage({ id: `App.${orderStatus.bill.action}` })}</span>
                                <span />
                            </>
                        )}
                    </Button>
                    <Button
                      className={classnames(
                        theme.statusButton,
                        theme.button,
                        {
                            [theme.disabled]: orderStatus.preparation.isDisabled,
                            [theme.active]: orderStatus.preparation.isActive
                        }
                      )}
                      onClick={showOrders}
                    >
                        <span className={classnames('icon-preparation', {
                                [theme.disabled]: orderStatus.preparation.isDisabled
                            })}
                        />
                        {orderStatus.preparation.action && (
                            <span className={theme.description}>{intl.formatMessage({ id: `App.${orderStatus.preparation.action}` })}</span>
                        )}
                        {Boolean(orderStatus.preparation.uncompletedOrderPositions) && (
                            <span className={classnames({
                                [theme.positions]: orderStatus.preparation.action,
                                [theme.badge]: !orderStatus.preparation.action
                            })}
                            >{orderStatus.preparation.uncompletedOrderPositions}</span>
                        )}
                    </Button>
                    <Button
                      className={classnames(theme.button, theme.orderButton, {
                        [theme.active]: orderStatus.order.isActive
                      })}
                      accent={orderStatus.order.isActive}
                      raised={orderStatus.order.isActive}
                      onClick={showOrders}
                      disabled={orderStatus.order.isDisabled}
                    >
                        <span className={classnames('icon-basket', {
                                [theme.disabled]: orderStatus.order.isDisabled
                            })}
                        />
                        {orderStatus.order.action && (
                            <span className={theme.description}>{intl.formatMessage({ id: `App.${orderStatus.order.action}` })}</span>
                        )}
                        {Boolean(orderStatus.order.newOrderPositions) && (
                            <span className={theme.positions}>{orderStatus.order.newOrderPositions}</span>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

OrderStatus.propTypes = {
    emitMessage: PropTypes.func.isRequired,
    dismissFirstMessage: PropTypes.func.isRequired,
    context: PropTypes.shape({
        sessionId: PropTypes.string.isRequired,
        routeType: PropTypes.number.isRequired
    }),
    connectOrdersSocket: PropTypes.func.isRequired,
    disconnectOrdersSocket: PropTypes.func.isRequired,
    connectBillSocket: PropTypes.func.isRequired,
    disconnectBillSocket: PropTypes.func.isRequired,
    restaurant: PropTypes.shape({
        name: PropTypes.string.isRequired
    }),
    table: PropTypes.shape({
        name: PropTypes.string,
        number: PropTypes.number
    }),
    notification: PropTypes.object,
    pushNotification: PropTypes.func.isRequired,
    reinitialize: PropTypes.bool,
    initialize: PropTypes.func,
    fingerprint: PropTypes.string,
    sessionError: PropTypes.bool.isRequired,
    slug: PropTypes.string.isRequired,
    clearMessages: PropTypes.func,
    isAnyOrder: PropTypes.bool,
    orderStatus: PropTypes.shape({
        bill: PropTypes.shape({
            isActive: PropTypes.bool,
            isDisabled: PropTypes.bool,
            action: PropTypes.string
        }),
        preparation: PropTypes.shape({
            isDisabled: PropTypes.bool,
            isActive: PropTypes.bool,
            uncompletedOrderPositions: PropTypes.number,
            action: PropTypes.string
        }),
        order: PropTypes.shape({
            isActive: PropTypes.bool,
            isDisabled: PropTypes.bool,
            action: PropTypes.string,
            newOrderPositions: PropTypes.number
        })
    }),
    shouldShowSessionEndMessage: PropTypes.bool
};

export default OrderStatus;
